import {
  Step,
  StepLabel,
  Stepper
} from '@mui/material';
import { calculateLocatinVars } from '../../../libs/browser';
import { parseError } from '../../../libs/parser';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import BackCloseBarComponent from '../../../components/BackCloseBarComponent';
import Context from '../../../provider';
import IconBenefitsRed from '../../../assets/hkdc/icons/BenefitsRedIcon';
import IconSettingTag from '../../../assets/hkdc/icons/SettingTagIcon';
import React, { useContext, useEffect, useRef, useState } from 'react';
import TierFormGroupComponent from '../../../components/TierFormGroupComponent';
import TierInfoListCheckComponent from '../../../components/TierInfoListCheckComponent';
import TierInfoSubmitComponent from '../../../components/TierInfoSubmitComponent';
import config from '../../../config';
import memberApi from '../../../libs/api/memberApi';
import useIsScrolling from '../../../hooks/useIsScrolling';
import useNotificationsCount from '../../../hooks/useNotificationsCount';
import './index.scss';

const steps = [
  'common.personal-info',
  'registration.step-3-title',
  'registration.step-4-title'
];

export default function MobileRenewalPayment() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const locationVars = calculateLocatinVars(location.pathname);

  const notificationNotReadCount = useNotificationsCount();

  const { init, me, dispatch, appConfig, registrationData } = useContext(Context);

  const containerRef = useRef(null);
  const isScrolling = useIsScrolling(containerRef.current);

  const [activeStep, setActiveStep] = React.useState(0);
  const [completed, setCompleted] = React.useState({});
  const [membershipRecord, setMembershipRecord] = useState(null);
  const [pageContentData, setPageContentData] = useState({});

  const [formFile, setFormFile] = useState(null);
  const [paymentFile, setPaymentFile] = useState(null);

  const fetchUserMembershipData = async (reg_id) => {
    try {
      dispatch({ type: 'SET_BACKDROP_LOADING', payload: true });
      const response = await memberApi.getUserMembershipRecord(reg_id);
      setMembershipRecord(response.data);

    } catch (error) {
      dispatch({
        type: 'SET_ERROR_DIALOG',
        payload: { open: true, ...parseError(error) }
      });
    } finally {
      dispatch({ type: 'SET_BACKDROP_LOADING', payload: false });
    }
  };

  const fetchPageContentData = async () => {
    try {
      dispatch({ type: 'SET_BACKDROP_LOADING', payload: true });
      const page = await memberApi.getPage({ ruri: `payment-term` });
      setPageContentData(page);
    } catch (error) {
      dispatch({
        type: 'SET_ERROR_DIALOG',
        payload: { open: true, ...parseError(error) }
      });
    } finally {
      dispatch({ type: 'SET_BACKDROP_LOADING', payload: false });
    }
  };

  const renewMembershipsRecord = async (record) => {
    try {
      dispatch({ type: 'SET_BACKDROP_LOADING', payload: true });
      const response = await memberApi.renewMembershipsRecord(record);
      setMembershipRecord(response.data);
      handleComplete();
    } catch (error) {
      dispatch({
        type: 'SET_ERROR_DIALOG',
        payload: { open: true, ...parseError(error) }
      });
    } finally {
      dispatch({ type: 'SET_BACKDROP_LOADING', payload: false });
    }
  };

  const submitRegisterRecord = async (record) => {
    try {
      dispatch({ type: 'SET_BACKDROP_LOADING', payload: true });
      const response = await memberApi.submitMembershipsRecord(record);

      navigate(`${config.basename}success`, {
        replace: true,
        state: {
          type: 'registration',
          title: 'success.registration-title',
          desc: 'success.registration-desc'
        }
      });
    } catch (error) {
      dispatch({
        type: 'SET_ERROR_DIALOG',
        payload: { open: true, ...parseError(error) }
      });
    } finally {
      dispatch({ type: 'SET_BACKDROP_LOADING', payload: false });
    }
  };

  useEffect(() => {
    setActiveStep(0);
  }, []);

  // This page only allow logged in user
  useEffect(() => {
    if (init && me?.status === 'activated') {
      fetchPageContentData();

      if (registrationData.isSave) {
        setMembershipRecord(registrationData.data);
        registrationData.formFile && setFormFile(registrationData.formFile);
        for (let index = 0; index < registrationData.step; index++) {
          const newCompleted = completed;
          newCompleted[index] = true;
          setCompleted(newCompleted);
        }

        setActiveStep(registrationData.step);
      } else {
        fetchUserMembershipData(me.loyalty_account?.membership_reg_id);
      }
    }
  }, [init, me, navigate]);

  useEffect(() => {
    containerRef.current.scrollTo(0, 0);
  }, [activeStep]);

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleComplete = () => {
    const newCompleted = completed;
    newCompleted[activeStep] = true;
    setCompleted(newCompleted);
    handleNext();
  };

  const getRegistrationStyle = (step) => {
    switch (step) {
      case 0:
      case 1:
        return {
          padding: '2rem',
          gap: '1.5rem'
        };
      case 2:
        return {
          padding: '2rem',
          gap: '2rem'
        };
      default:
        return {};
    }
  };

  return (
    (
      <div className='mobile-renewal-page'>
        <BackCloseBarComponent
          onClick={() => {
            if (activeStep === 0) {
              navigate(-1);
            } else {
              setActiveStep(activeStep - 1);
            }
          }}
          isBack={true}
        />

        <div ref={containerRef} className="content-container">
          <div className="setting-title-container">
              <div className="title-text-cont">
                <div className="title-text">
                  {t('home-after.shortcut-3')}
                </div>
                <div className="step-cont">
                  <Stepper nonLinear activeStep={activeStep}>
                    {steps.map((label, index) => {
                      return (
                        <Step key={index} active={completed[index]}>
                          <StepLabel />
                        </Step>
                      );
                    })}
                  </Stepper>
                </div>
              </div>
              <IconSettingTag
                className="setting-tag-icon"
                size="large" />
          </div>

          {
            init && membershipRecord && activeStep === 0 &&
            <div className="tier-show-section" style={{ backgroundColor:  me ? `${me.loyalty_account?.tier?.color}` : undefined }}>
              <div className="show-text">
                {t(`tier-name.${membershipRecord.membership_type}`)}
              </div>
            </div>
          }

          <div className="registration-content-container"
            style={getRegistrationStyle(activeStep)}
          >
            <div className="content-title-container">
              <div className="icon-container">
                <IconBenefitsRed
                  className="icon"
                  size="large" />
              </div>
              <div className="title-text">
                {t(steps[activeStep])}
              </div>
            </div>
            {
              init && membershipRecord && activeStep === 0 &&
              <>
                <div className="compulsory-text">{t('registration.step-2-remark')}</div>
                <TierFormGroupComponent
                  membershipRecord={membershipRecord}
                  tierType={membershipRecord.membership_type}
                  fileInfo={formFile}
                  countryCode={ appConfig ? appConfig.mobile_country_codes[0] : '+852' }
                  onNext= {(record, file) => {
                    setFormFile(file);
                    renewMembershipsRecord(record);
                  }}
                />
              </>
            }
            {
              init && membershipRecord && activeStep === 1 &&
              <TierInfoListCheckComponent
                membershipRecord={membershipRecord}
                tierType={membershipRecord.membership_type}
                tierColor={me.loyalty_account?.tier?.color}
                fileInfo={formFile}
                onNext= {(record) => {
                  if (record.membership_fee === 0) {
                    submitRegisterRecord(record);
                  } else {
                    renewMembershipsRecord(record);
                  }
                }}
              />
            }
            {
              init && membershipRecord && activeStep === 2 &&
              <TierInfoSubmitComponent
                membershipRecord={membershipRecord}
                content={pageContentData}
                fileInfo={paymentFile}
                onUploadFile={(file) => {
                  if (file) {
                    setPaymentFile(file);
                  } else {
                    setPaymentFile(null);
                  }
                }}
                onNext= {(record) => {
                  submitRegisterRecord(record);
                }}
              />
            }
          </div>
          <div style={{ height: '10rem' }} />
        </div>
      </div>
    )
  );
}